import { ListParams } from "../models/listParams";
import { LayerConfiguration } from "../models/maps/layerConfiguration";
import { LayerDetails } from "../models/maps/layerDetails";
import { Beoordeling } from "../models/plan/beoordeling";
import { GeoPackageType } from "../models/types/geoPackageType";
import { authService } from "../services/auth.service";
import { BaseWebService } from "./base.web.service";

export class PlanManagementWebService extends BaseWebService {

    // Azure maps

    public async getAzureMapsToken(organizationId: string): Promise<{ token: string; expiresAtUtc: string }> {
        const headers = await this.getHeadersJson(); // Await the promise here
        const url = `${this.appConfig.servicePlanManagementUrl}/api/azure-maps/${organizationId}/token`;
        return this.makeRequest(url, 'GET', headers);
    }

    // GeoPackage Asset en Plan

    private getGeoPackageBaseUrl(type: GeoPackageType, organizationId?: string): string {
        switch (type) {
          case GeoPackageType.ASSET:
            return `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-geopackage`;
          case GeoPackageType.PLAN:
          default:
            return `${this.appConfig.servicePlanManagementUrl}/api/geopackage/${organizationId}`;
        }
    }

    public async uploadGeoPackage(type: GeoPackageType, organizationId: string, file: File) {
        const formData = new FormData();
        formData.append("file", file);
      
        const token = await authService.getAccessToken();
      
        const headers: HeadersInit = {
          Accept: 'application/json',
          ...(token && { Authorization: 'Bearer ' + token })
        };

        if (type === GeoPackageType.ASSET && organizationId) {
            headers['x-Organization-ID'] = organizationId;
        }
      
        const baseUrl = this.getGeoPackageBaseUrl(type, organizationId);
        const url = `${baseUrl}/upload`;
      
        return this.makeRequest(url, 'POST', headers, formData);
    }
    
    public async getGeoPackageStatus(type: GeoPackageType, organizationId: string) {
        const headers = await this.getHeadersJson(true, organizationId);
        const baseUrl = this.getGeoPackageBaseUrl(type, organizationId);
        const url = `${baseUrl}/status`;
      
        return this.makeRequest(url, 'GET', headers);
    }
      
    public async getGeoPackageData(type: GeoPackageType, organizationId: string, timestamp: string) {
        const headers = await this.getHeadersJson(true, organizationId);
        const baseUrl = this.getGeoPackageBaseUrl(type, organizationId);
        const url = `${baseUrl}/status/${timestamp}`;
      
        return this.makeRequest(url, 'GET', headers);
    }
      
    public async processGeoPackage(type: GeoPackageType, organizationId: string, timestamp: string) {
        const headers = await this.getHeadersJson(true, organizationId);
        const baseUrl = this.getGeoPackageBaseUrl(type, organizationId);
        const url = `${baseUrl}/process/${timestamp}`;
        
        return this.makeRequest(url, 'POST', headers);
    }
      
    public async deleteGeoPackage(type: GeoPackageType, organizationId: string, timestamp: string) {
        const headers = await this.getHeadersJson(true, organizationId);
        const baseUrl = this.getGeoPackageBaseUrl(type, organizationId);
        const url = `${baseUrl}/delete/${timestamp}`;
        
        return this.makeRequest(url, 'DELETE', headers, undefined, 0, true);
    }

    // Asset Layer Configuration

    public async getAssetLayerConfigurations(organizationId: string, listParams: ListParams): Promise<any> {
        const params = this.createListParams(listParams);
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layer-configurations${params}`;
      
        return this.makeRequest<any>(url, 'GET', headers);
    }

    public async getAssetLayerConfiguration(assetLayerId: string, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layer-configurations/${assetLayerId}`;
        return this.makeRequest<any>(url, 'GET', headers);
    }

    public async putAssetLayerConfiguration(assetLayerId: string, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layer-configurations/${assetLayerId}`;
        return this.makeRequest<any>(url, 'PUT', headers, undefined, 0, true);
    }

    public async deleteAssetLayerConfiguration(assetLayerId: string, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layer-configurations/${assetLayerId}`;
        return this.makeRequest<any>(url, 'DELETE', headers, undefined, 0, true); // wrapResponse: true
    }

    // Asset Layers

    public async getAssetLayers(organizationId: string, listParams: ListParams): Promise<any> {
        const params = this.createListParams(listParams);
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layers${params}`;
      
        return this.makeRequest<any>(url, 'GET', headers);
    }

    public async getAssetLayer(assetLayerId: string, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layers/${assetLayerId}`;
        return this.makeRequest<any>(url, 'GET', headers);
    }

    public async deleteAssetLayer(assetLayerId: string, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layers/${assetLayerId}`;
        return this.makeRequest<any>(url, 'DELETE', headers, undefined, 0, true);
    }

    public async putAssetLayerShareTarget(assetLayerId: string, targetOrganizationId: string, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layers/${assetLayerId}/share/${targetOrganizationId}`;
        return this.makeRequest<any>(url, 'PUT', headers, undefined, 0, true);
    }

    public async deleteAssetLayerShareTarget(assetLayerId: string, targetOrganizationId: string, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layers/${assetLayerId}/share/${targetOrganizationId}`;
        return this.makeRequest<any>(url, 'DELETE', headers, undefined, 0, true); // wrapResponse: true
    }

    public async putAssetLayerShareAsPlan(assetLayerId: string, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layers/${assetLayerId}/use-plan-share-settings`;
        return this.makeRequest<any>(url, 'PUT', headers, undefined, 0, true);
    }

    public async deleteAssetLayerShareAsPlan(assetLayerId: string, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layers/${assetLayerId}/use-plan-share-settings`;
        return this.makeRequest<any>(url, 'DELETE', headers, undefined, 0, true); // wrapResponse: true
    }

    public async putAssetLayerDetails(assetLayerId: string, details: LayerDetails, organizationId: string): Promise<any> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/asset-layers/${assetLayerId}/details`;
        const body = details;
        return this.makeRequest<any>(url, 'PUT', headers, body, 0, true);
    }

    // Public layers delen (organisatie)

    public async getLayerConfigurations(organizationId: string,listParams: ListParams): Promise<any> {
        const params = this.createListParams(listParams);
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/layer-configurations${params}`;
      
        return this.makeRequest<any>(url, 'GET', headers);
    }

    public async getLayerConfiguration(layerId: string, organizationId: string): Promise<LayerConfiguration> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/layer-configurations/${layerId}`;
        return this.makeRequest<LayerConfiguration>(url, 'GET', headers);
    }

    public async putLayerConfiguration(layerId: string, organizationId: string): Promise<LayerConfiguration> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/layer-configurations/${layerId}`;
        return this.makeRequest<LayerConfiguration>(url, 'PUT', headers, undefined, 0, true);
    }
    
    public async deleteLayerConfiguration(layerId: string, organizationId: string): Promise<LayerConfiguration> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/layer-configurations/${layerId}`;
        return this.makeRequest<LayerConfiguration>(url, 'DELETE', headers, undefined, 0, true); // wrapResponse: true
    }

    // Layers

    public async getLayers(organizationId: string, listParams: ListParams): Promise<any> {
        const params = this.createListParams(listParams);
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/layers${params}`;
      
        return this.makeRequest<any>(url, 'GET', headers);
    }

    public async getLayer(layerId: string, organizationId: string): Promise<LayerConfiguration> {
        const headers = await this.getHeadersJson(true, organizationId);
        const url = `${this.appConfig.servicePlanManagementUrl}/api/v1/layers/${layerId}`;
        return this.makeRequest<LayerConfiguration>(url, 'GET', headers);
    }

    // Plan Beoordeling

    public async getPlanBeoordelingen(infplId: string, organizationId: string, listParams: any = null) {
        const params = this.createListParams(listParams);
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/beoordelingen/${organizationId}/plannen/${infplId}${params}`;
        return this.makeRequest(url, 'GET', headers);
    }

    public async putPlanBeoordeling(beoordeling: Beoordeling, infplId: string, organizationId: string) {
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/beoordelingen/${organizationId}/plannen/${infplId}`;
        const body = beoordeling;
        return this.makeRequest(url, 'PUT', headers, body, 0, true);
    }
    
    // Gebied Sharing

    public async getOrganisationsToShare(listParams: ListParams) {
        const params = this.createListParams(listParams);
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/sharing/organizations${params}`;
        return this.makeRequest(url, 'GET', headers);
    }

    public async getSharedOrganisations(organizationId: string, listParams: ListParams) {
        const params = this.createListParams(listParams);
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/sharing/${organizationId}/organizations${params}`;
        return this.makeRequest(url, 'GET', headers);
    }

    public async putSharedOrganisation(sharedId: string, organizationId: string) {
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/sharing/${organizationId}/organizations/${sharedId}`;
        return this.makeRequest(url, 'PUT', headers, undefined, 0, true);
    }
    
    public async deleteSharedOrganisation(sharedId: string, organizationId: string) {
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/sharing/${organizationId}/organizations/${sharedId}`;
        return this.makeRequest(url, 'DELETE', headers, undefined, 0, true); // wrapResponse: true
    }

    // Plan Sharing

    public async getOrganisatiesToSharePlans(listParams: ListParams) {
        const params = this.createListParams(listParams);
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/plan-sharing/organizations${params}`;
        return this.makeRequest(url, 'GET', headers);
    }

    public async getOrganisationsSharedWithPlan(infplId: string, organizationId: string, listParams: ListParams) {
        const params = this.createListParams(listParams);
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/plan-sharing/${organizationId}/plan/${infplId}/organizations${params}`;
        return this.makeRequest(url, 'GET', headers);
    }

    public async putSharePlanWithOrganisation(infplId: string, sharedId: string, organizationId: string) {
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/plan-sharing/${organizationId}/plan/${infplId}/organizations/${sharedId}`;
        return this.makeRequest(url, 'PUT', headers, undefined, 0, true);
    }

    public async deleteSharePlanWithOrganisation(infplId: string, sharedId: string, organizationId: string) {
        const headers = await this.getHeadersJson();
        const url = `${this.appConfig.servicePlanManagementUrl}/api/plan-sharing/${organizationId}/plan/${infplId}/organizations/${sharedId}`;
        return this.makeRequest(url, 'DELETE', headers, undefined, 0, true); // wrapResponse: true
    }
    
}